/**
 * Participants table filters: Inscripción and Pago stay visible (the daily ones); the rest live in
 * a "Más filtros" popover. Active "more" filters show as removable chips so nothing is hidden.
 */

import { useMemo } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { District } from '@/models/district';
import type { Region } from '@/models/region';
import { activeFilterChips, type ParticipantTabFilters } from '../utils/participantFilters';

interface ParticipantFilterBarProps {
  filters: ParticipantTabFilters;
  onChange: (update: (prev: ParticipantTabFilters) => ParticipantTabFilters) => void;
  districts: District[];
  /** Regions already narrowed to the selected district (all when none) */
  regions: Region[];
  hasActiveFilters: boolean;
  /** Needs permission to see payments: the receipt filter is built from them */
  canFilterReceipts?: boolean;
  /** Digital-signature event: the "Sin firma" filter makes sense */
  canFilterSignature?: boolean;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

export function ParticipantFilterBar({ filters, onChange, districts, regions, hasActiveFilters, canFilterReceipts = false, canFilterSignature = false }: ParticipantFilterBarProps) {
  const chips = useMemo(
    () =>
      activeFilterChips(filters, {
        district: districts.find((d) => d.id === filters.district_id)?.name ?? undefined,
        region: regions.find((r) => r.id === filters.region_id)?.name ?? undefined,
      }),
    [filters, districts, regions],
  );
  // A district chip already covers its region: don't count the pair twice
  const moreCount = chips.filter((c) => !(c.key === 'region' && filters.district_id)).length;

  const set = (patch: Partial<ParticipantTabFilters>) => onChange((prev) => ({ ...prev, ...patch }));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Inscripción">
          <Select
            value={filters.registration_status ?? 'ALL'}
            onValueChange={(v) => set({ registration_status: v === 'ALL' ? undefined : (v as ParticipantTabFilters['registration_status']) })}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todas</SelectItem>
              <SelectItem value="PRE_REGISTERED">Por revisar</SelectItem>
              <SelectItem value="VERIFIED">Verificadas</SelectItem>
              <SelectItem value="REJECTED">Rechazadas</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Field label="Pago">
          <Select
            value={filters.payment_status ?? 'ALL'}
            onValueChange={(v) => set({ payment_status: v === 'ALL' ? undefined : (v as ParticipantTabFilters['payment_status']) })}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos los pagos</SelectItem>
              <SelectItem value="PAID">Pagados</SelectItem>
              <SelectItem value="PARTIAL">Parciales</SelectItem>
              <SelectItem value="PENDING">Pendientes</SelectItem>
              <SelectItem value="REVERSED">Revertidos</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <SlidersHorizontal className="size-4" />
              Más filtros
              {moreCount > 0 && <Badge variant="secondary" className="px-1.5">{moreCount}</Badge>}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[320px] space-y-3">
            <Field label="Tipo">
              <Select
                value={filters.type || 'ALL'}
                onValueChange={(v) => set({ type: v as ParticipantTabFilters['type'] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  <SelectItem value="MINISTER">Solo ministros</SelectItem>
                  <SelectItem value="DELEGATE">Solo delegados</SelectItem>
                  <SelectItem value="GUEST">Solo invitados</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Asistencia">
              <Select
                value={filters.attendance_status ?? 'ALL'}
                onValueChange={(v) => set({ attendance_status: v === 'ALL' ? undefined : (v as ParticipantTabFilters['attendance_status']) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  <SelectItem value="ATTENDED">Asistieron</SelectItem>
                  <SelectItem value="PENDING">Pendientes</SelectItem>
                  <SelectItem value="ABSENT">Ausentes</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Obligatoriedad">
              <Select
                value={filters.is_mandatory === undefined ? 'ALL' : filters.is_mandatory ? 'YES' : 'NO'}
                onValueChange={(v) => set({ is_mandatory: v === 'ALL' ? undefined : v === 'YES' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  <SelectItem value="YES">Obligatorios</SelectItem>
                  <SelectItem value="NO">Opcionales</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {canFilterReceipts && (
              <Field label="Recibo">
                <Select
                  value={filters.missing_receipt ? 'MISSING' : 'ALL'}
                  onValueChange={(v) => set({ missing_receipt: v === 'MISSING' ? true : undefined })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos</SelectItem>
                    <SelectItem value="MISSING">Pagos sin recibo</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}

            {canFilterSignature && (
              <Field label="Firma">
                <Select
                  value={filters.missing_signature ? 'MISSING' : 'ALL'}
                  onValueChange={(v) => set({ missing_signature: v === 'MISSING' ? true : undefined })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos</SelectItem>
                    <SelectItem value="MISSING">Sin firma</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}

            <Field label="Distrito">
              <Select
                value={filters.district_id ?? 'ALL'}
                onValueChange={(v) => set({ district_id: v === 'ALL' ? undefined : v, region_id: undefined })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos los distritos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos los distritos</SelectItem>
                  {districts.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Región">
              <Select
                value={filters.region_id ?? 'ALL'}
                disabled={!filters.district_id}
                onValueChange={(v) => set({ region_id: v === 'ALL' ? undefined : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={filters.district_id ? 'Todas las regiones' : 'Seleccione un distrito'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todas las regiones</SelectItem>
                  {regions.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </PopoverContent>
        </Popover>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={() => onChange(() => ({ type: 'ALL' }))}>
            Limpiar filtros
          </Button>
        )}
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <Badge key={chip.key} variant="secondary" className="gap-1 pr-1">
              {chip.label}
              <button
                type="button"
                aria-label={`Quitar filtro ${chip.label}`}
                className="rounded-sm p-0.5 hover:bg-background/60"
                onClick={() => set(chip.clear)}
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
